import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, Check } from "lucide-react";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { ButtonRouter } from "~/components/ui/button";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
  createFaqStructuredData,
} from "~/lib/shared/meta";

const CANONICAL_URL = `${BRAND_URL}/dcm-vs-betterup`;
const PAGE_TITLE = "Dream Coach Match vs. BetterUp";

const COMPARISON_ROWS = [
  {
    label: "Designed for",
    dcm: "Individuals choosing a coach for their own life and work.",
    betterUp: "Organizations offering coaching as an employee benefit.",
  },
  {
    label: "How you begin",
    dcm: "Browse public profiles or answer a few questions to start a free match.",
    betterUp:
      "Enroll through your employer, complete onboarding, then receive coach options.",
  },
  {
    label: "Who pays",
    dcm: "Matching is free. You pay only when you choose to book coaching.",
    betterUp: "Your organization purchases access to the programme.",
  },
  {
    label: "Choosing a coach",
    dcm: "Explore approaches, experience, and fit before you decide.",
    betterUp:
      "Choose from recommendations within an employer-provided platform.",
  },
  {
    label: "Coach details",
    dcm: "Profiles make each coach's background, focus, and credentials easy to review.",
    betterUp:
      "Coach options and profiles are shown after your programme onboarding.",
  },
  {
    label: "The relationship",
    dcm: "A direct engagement you shape with the coach you choose.",
    betterUp: "A coaching benefit available through your employer's programme.",
  },
  {
    label: "Useful when",
    dcm: "You want personal choice around a transition, goal, or question that matters to you.",
    betterUp:
      "Your employer provides access and you want coaching within that benefit.",
  },
] as const;

const LIFE_MOMENTS = [
  "You are deciding whether to stay in a role that no longer fits.",
  "You are starting something of your own and want a coach who understands the stakes.",
  "A relationship, health, or money decision is affecting every part of your life.",
  "You have outgrown an old definition of success and want room to name what comes next.",
] as const;

const DECISIONS = [
  {
    title: "Your employer already offers BetterUp",
    description:
      "Use the benefit. It can be a thoughtful, practical way to access coaching. If you also want a relationship you choose and manage independently, Dream Coach Match gives you another path.",
  },
  {
    title: "You are looking for a coach yourself",
    description:
      "Dream Coach Match lets you begin with the work you want to do, explore coaches openly, and only pay when you decide to book.",
  },
  {
    title: "You support a small team",
    description:
      "A direct coach relationship can be a simple option when each person needs a different kind of support and personal choice matters.",
  },
] as const;

const FAQS = [
  {
    question: "Can I still buy BetterUp as an individual?",
    answer:
      "No. BetterUp stopped accepting new individual subscriptions and new individual coaching-session purchases on January 31, 2026. Its existing individual offering is scheduled to end on January 31, 2027.",
  },
  {
    question: "Is Dream Coach Match free to use?",
    answer:
      "Using Dream Coach Match to explore coaches and find a match is free. You only pay if you decide to book sessions with a coach.",
  },
  {
    question: "How does Dream Coach Match help me choose a coach?",
    answer:
      "You can browse public coach profiles or use the Coach Finder to share what you are working through. It helps you discover coaches whose focus and approach fit the kind of change you want to make.",
  },
  {
    question: "Can I review a coach's credentials before I reach out?",
    answer:
      "Yes. Coach profiles are designed to make their background, areas of focus, and credentials visible while you explore your options.",
  },
  {
    question: "Does BetterUp coaching stay confidential from my employer?",
    answer:
      "BetterUp says coaching conversations and personal details are confidential. Employers receive limited usage information for programme administration, not the contents of coaching sessions.",
  },
  {
    question: "Can Dream Coach Match help with more than career coaching?",
    answer:
      "Yes. You can look for support around work, relationships, wellbeing, leadership, confidence, life transitions, and the goals that cut across them.",
  },
] as const;

export const Route = createFileRoute("/_public/dcm-vs-betterup")({
  staticData: { title: PAGE_TITLE },
  head: () => ({
    ...createBrandMeta({
      title: PAGE_TITLE,
      description:
        "Compare Dream Coach Match and BetterUp: individual coach choice, free matching, employer-provided coaching, and the right fit for your next step.",
      canonical: CANONICAL_URL,
      ogUrl: CANONICAL_URL,
      ogImage:
        "https://dreamcoachmatch.com/img/dcm-vs-betterup-coaching-conversation.png",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(createFaqStructuredData(FAQS)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: PAGE_TITLE, url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  component: DreamCoachMatchVsBetterUpPage,
});

function DreamCoachMatchVsBetterUpPage() {
  return (
    <main className="overflow-hidden bg-[#fbfaff] text-gray-950">
      <section className="relative isolate border-b border-violet-100">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-48 left-[28%] -z-10 h-[42rem] w-[42rem] rounded-full bg-violet-200/45 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-24 -right-52 -z-10 h-96 w-96 rounded-full bg-fuchsia-100/70 blur-3xl"
        />
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-8 md:pb-20 xl:px-0">
          <Breadcrumb
            items={[{ label: "Home", href: "/" }, { label: PAGE_TITLE }]}
            isBold={false}
          />
          <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-16">
            <div className="max-w-xl">
              <p className="text-xs font-medium tracking-[0.2em] text-violet-700 uppercase">
                {PAGE_TITLE}
              </p>
              <h1 className="mt-5 font-heading text-5xl leading-[0.98] font-medium tracking-[-0.04em] text-balance text-gray-950 md:text-6xl lg:text-7xl">
                Your coach should fit your life.
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-gray-700 md:text-xl">
                Find coaching built around your goals, your voice, and the
                person you choose to work with.
              </p>
              <div className="mt-8">
                <ButtonRouter
                  to="/discovery"
                  variant="primary"
                  size="lg"
                  className="group gap-2 shadow-[0_16px_35px_rgba(91,33,182,0.22)] transition-transform hover:-translate-y-0.5 active:translate-y-px"
                >
                  Find your coach
                  <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                </ButtonRouter>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-2xl lg:max-w-none">
              <div className="absolute -inset-5 -z-10 rounded-[2rem] bg-violet-200/50 blur-2xl" />
              <div className="overflow-hidden rounded-[1.75rem] bg-violet-100 p-2 shadow-[0_30px_80px_rgba(63,29,105,0.18)]">
                <img
                  src="/img/dcm-vs-betterup-coaching-conversation.webp"
                  alt="A coach and client in a thoughtful one-to-one conversation"
                  width={1440}
                  height={1080}
                  fetchPriority="high"
                  className="aspect-[4/3] w-full rounded-[1.35rem] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:py-24 xl:px-0">
        <div className="max-w-2xl">
          <p className="text-sm text-gray-500">Updated August 2026</p>
          <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
            Similar promise. Different starting point.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-gray-700">
            BetterUp and Dream Coach Match both make coaching more accessible.
            BetterUp is built for organizations supporting their employees.
            Dream Coach Match is a place for individuals to choose a coach for
            themselves.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-[1.5rem] border border-violet-100 bg-white shadow-[0_16px_45px_rgba(54,27,103,0.07)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-separate border-spacing-0 text-left">
              <thead>
                <tr className="text-sm">
                  <th className="w-[24%] bg-gray-50 px-6 py-5 font-medium text-gray-500">
                    The experience
                  </th>
                  <th className="w-[38%] bg-violet-700 px-6 py-5 font-medium text-white">
                    Dream Coach Match
                  </th>
                  <th className="w-[38%] bg-[#f1f0f5] px-6 py-5 font-medium text-gray-800">
                    BetterUp
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="align-top">
                    <th className="border-t border-gray-100 bg-white px-6 py-5 text-sm font-medium text-gray-900">
                      {row.label}
                    </th>
                    <td className="border-t border-violet-100 bg-violet-50/50 px-6 py-5 text-sm leading-relaxed text-violet-950 md:text-base">
                      <span className="flex gap-3">
                        <Check
                          className="mt-1 h-4 w-4 shrink-0 text-violet-700"
                          aria-hidden
                        />
                        {row.dcm}
                      </span>
                    </td>
                    <td className="border-t border-gray-100 bg-white px-6 py-5 text-sm leading-relaxed text-gray-700 md:text-base">
                      {row.betterUp}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-500">
          This comparison reflects each service's public positioning as checked
          in August 2026. A workplace benefit can be valuable. The important
          question is whether its structure fits the kind of support you want.
        </p>
      </section>

      <section className="border-y border-violet-100 bg-violet-50/65">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] md:items-center md:py-24 xl:px-0">
          <div className="relative mx-auto w-full max-w-sm md:max-w-none">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-fuchsia-200/60 blur-2xl" />
            <img
              src="/img/dcm-vs-betterup-reflection.webp"
              alt="A person reflecting in a notebook after a coaching session"
              width={1122}
              height={1402}
              loading="lazy"
              className="aspect-[4/5] w-full rounded-[1.5rem] object-cover shadow-[0_24px_60px_rgba(63,29,105,0.16)]"
            />
          </div>
          <div className="max-w-2xl md:pl-8">
            <h2 className="font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
              Coaching is personal. The choice should be too.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-700">
              A workplace benefit can be a generous starting point. But when a
              decision reaches beyond your job, it can help to choose a coach
              through a relationship that belongs entirely to you.
            </p>
            <Link
              to="/coaches"
              className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-violet-800 transition-colors hover:text-violet-950"
            >
              Explore our coaches
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:py-24 xl:px-0">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20">
          <div>
            <p className="text-xs font-medium tracking-[0.2em] text-violet-700 uppercase">
              Access and cost
            </p>
            <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
              One is a workplace benefit. One starts with you.
            </h2>
          </div>
          <div className="grid gap-8 sm:grid-cols-2">
            <article className="border-t border-violet-200 pt-6">
              <p className="text-sm font-medium text-violet-700">
                Dream Coach Match
              </p>
              <h3 className="mt-3 font-heading text-2xl font-medium tracking-tight text-gray-950">
                Free to find your fit
              </h3>
              <p className="mt-3 leading-relaxed text-gray-700">
                You can explore coaches and use Coach Finder without paying. You
                only pay when you choose to book sessions with a coach.
              </p>
            </article>
            <article className="border-t border-gray-200 pt-6">
              <p className="text-sm font-medium text-gray-600">BetterUp</p>
              <h3 className="mt-3 font-heading text-2xl font-medium tracking-tight text-gray-950">
                Access through work
              </h3>
              <p className="mt-3 leading-relaxed text-gray-700">
                BetterUp is sold to organizations. In January 2026, it stopped
                accepting new individual subscriptions and session purchases.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 md:pb-24 xl:px-0">
        <div className="grid gap-12 border-t border-violet-100 pt-16 md:grid-cols-[1.05fr_0.95fr] md:pt-24">
          <div className="max-w-2xl">
            <p className="text-xs font-medium tracking-[0.2em] text-violet-700 uppercase">
              Choosing a coach
            </p>
            <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
              See the person before you choose the path.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-700">
              A coaching relationship works because of the conversation, not
              just the category. Dream Coach Match lets you review coaches'
              experience, focus, and approach while you are still deciding.
            </p>
          </div>
          <div className="space-y-7">
            <div className="border-l-2 border-violet-500 pl-6">
              <h3 className="font-heading text-xl font-medium tracking-tight text-gray-950">
                Browse before you commit
              </h3>
              <p className="mt-2 leading-relaxed text-gray-700">
                Explore the directory by coaching area, location, and the
                qualities you care about. Profiles make it easier to notice when
                a coach feels relevant.
              </p>
            </div>
            <div className="border-l-2 border-violet-200 pl-6">
              <h3 className="font-heading text-xl font-medium tracking-tight text-gray-950">
                Let your goal guide the match
              </h3>
              <p className="mt-2 leading-relaxed text-gray-700">
                Coach Finder begins with what is on your mind, then helps you
                discover coaches whose work may fit the next step.
              </p>
            </div>
            <div className="border-l-2 border-violet-200 pl-6">
              <h3 className="font-heading text-xl font-medium tracking-tight text-gray-950">
                Review the details that create trust
              </h3>
              <p className="mt-2 leading-relaxed text-gray-700">
                Our profiles surface each coach's credentials, experience, and
                areas of focus so you can make a more informed introduction.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 md:grid-cols-[0.9fr_1.1fr] md:py-24 xl:px-0">
          <div>
            <p className="text-xs font-medium tracking-[0.2em] text-violet-300 uppercase">
              A fair comparison
            </p>
            <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight md:text-5xl">
              BetterUp can be a strong fit when work is the doorway.
            </h2>
          </div>
          <div className="max-w-xl text-lg leading-relaxed text-gray-300">
            <p>
              If your employer provides BetterUp, use the benefit. Its
              onboarding is designed to recommend coaches after a self-
              assessment, and it gives organizations a structured way to offer
              coaching at scale.
            </p>
            <p className="mt-5">
              Dream Coach Match is not asking you to reject that support. It is
              for the times when you want to choose the coach, set the context,
              and make the relationship your own from the beginning.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:py-24 xl:px-0">
        <div className="max-w-3xl">
          <p className="text-xs font-medium tracking-[0.2em] text-violet-700 uppercase">
            The whole picture
          </p>
          <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
            Some questions are bigger than a performance review.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-gray-700">
            BetterUp says coaching conversations are confidential. Even so, a
            private coaching relationship can feel like the clearer place for
            choices you want to keep entirely separate from an employer benefit.
          </p>
        </div>
        <ol className="mt-10 grid gap-x-10 gap-y-0 md:grid-cols-2">
          {LIFE_MOMENTS.map((moment, index) => (
            <li
              key={moment}
              className="flex gap-5 border-t border-violet-100 py-6 text-lg leading-relaxed text-gray-800"
            >
              <span className="font-heading text-2xl font-medium text-violet-600">
                0{index + 1}
              </span>
              <span>{moment}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-violet-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:py-24 xl:px-0">
          <div className="max-w-2xl">
            <h2 className="font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
              Which choice is right for you?
            </h2>
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {DECISIONS.map((decision, index) => (
              <article
                key={decision.title}
                className="border-t border-violet-200 pt-6"
              >
                <p className="text-sm font-medium text-violet-700">
                  0{index + 1}
                </p>
                <h3 className="mt-3 font-heading text-2xl font-medium tracking-tight text-gray-950">
                  {decision.title}
                </h3>
                <p className="mt-3 leading-relaxed text-gray-700">
                  {decision.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 md:py-24 xl:px-0">
        <div className="max-w-2xl">
          <p className="text-xs font-medium tracking-[0.2em] text-violet-700 uppercase">
            Questions, answered
          </p>
          <h2 className="mt-4 font-heading text-4xl leading-tight font-medium tracking-tight text-gray-950 md:text-5xl">
            A few useful details before you choose.
          </h2>
        </div>
        <dl className="mt-10 divide-y divide-violet-100 border-y border-violet-100">
          {FAQS.map((faq) => (
            <div
              key={faq.question}
              className="grid gap-3 py-7 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] md:gap-10"
            >
              <dt className="font-heading text-xl font-medium tracking-tight text-gray-950">
                {faq.question}
              </dt>
              <dd className="leading-relaxed text-gray-700">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="bg-violet-800 text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:py-24 xl:px-0">
          <div className="max-w-3xl">
            <BadgeCheck className="h-7 w-7 text-violet-200" aria-hidden />
            <h2 className="mt-5 font-heading text-4xl leading-tight font-medium tracking-tight md:text-5xl">
              The right coach is someone you choose for the life you want to
              build.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-violet-100">
              Start by telling us what is on your mind. We will help you
              discover coaches whose experience and approach fit the journey
              ahead.
            </p>
            <ButtonRouter
              to="/discovery"
              variant="default"
              size="lg"
              className="group mt-8 gap-2 bg-white text-violet-800 hover:bg-violet-50"
            >
              Start your free match
              <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
            </ButtonRouter>
          </div>
        </div>
      </section>
    </main>
  );
}
